import React, { useMemo, useState } from "react";
import {
  FaExclamationTriangle,
  FaHistory,
  FaMedal,
  FaRunning,
  FaTimes,
  FaTrash,
  FaTrophy,
  FaUsers,
} from "react-icons/fa";

import { getFullName } from "../utils/judokaProfileUtils";

function DeleteJudokaModal({ judoka, deleting, onClose, onConfirm }) {
  const [confirmText, setConfirmText] = useState("");

  const judokaName = useMemo(() => getFullName(judoka), [judoka]);

  const canDelete = confirmText.trim().toUpperCase() === "SUPPRIMER";

  if (!judoka) return null;

  const impactedItems = [
    {
      icon: FaHistory,
      label: "Historique des passages de ceinture",
    },
    {
      icon: FaTrophy,
      label: "Palmarès du judoka",
    },
    {
      icon: FaUsers,
      label: "Liens avec les groupes",
    },
    {
      icon: FaMedal,
      label: "Inscriptions aux événements et résultats",
    },
    {
      icon: FaRunning,
      label: "Suivi de préparation physique",
    },
  ];

  const handleSubmit = (event) => {
    event.preventDefault();

    if (!canDelete || deleting) return;

    onConfirm();
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-center bg-slate-950/70 px-4 py-6 backdrop-blur-sm sm:items-center">
      <form
        onSubmit={handleSubmit}
        className="w-full max-w-2xl overflow-hidden rounded-[2rem] border border-red-200 bg-white shadow-2xl shadow-red-950/20 dark:border-red-400/20 dark:bg-slate-950"
      >
        <div className="relative overflow-hidden bg-gradient-to-br from-red-700 via-red-600 to-orange-600 p-6 text-white sm:p-7">
          <div className="absolute -right-12 -top-12 h-40 w-40 rounded-full bg-white/10" />
          <div className="absolute -bottom-16 left-10 h-44 w-44 rounded-full bg-black/10" />

          <div className="relative flex items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-3xl bg-white/15 text-white shadow-lg">
                <FaExclamationTriangle className="text-2xl" />
              </div>

              <div>
                <p className="text-xs font-bold uppercase tracking-[0.22em] text-red-100">
                  Suppression définitive
                </p>

                <h2 className="mt-2 font-titre text-2xl font-bold sm:text-3xl">
                  Supprimer {judokaName}
                </h2>

                <p className="mt-3 max-w-xl text-sm font-semibold leading-7 text-white/80">
                  Cette action supprimera le profil judoka et tous les liens
                  associés. Elle ne pourra pas être annulée depuis l’interface.
                </p>
              </div>
            </div>

            <button
              type="button"
              onClick={onClose}
              disabled={deleting}
              className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-white/10 text-white transition hover:bg-white/20 disabled:cursor-not-allowed disabled:opacity-50"
              aria-label="Fermer"
            >
              <FaTimes />
            </button>
          </div>
        </div>

        <div className="max-h-[70vh] overflow-y-auto p-6 sm:p-7">
          <div className="rounded-[1.6rem] border border-red-200 bg-red-50 p-4 dark:border-red-400/20 dark:bg-red-500/10">
            <p className="text-sm font-bold text-red-800 dark:text-red-100">
              Données qui seront supprimées avec ce profil :
            </p>

            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {impactedItems.map((item) => {
                const Icon = item.icon;

                return (
                  <div
                    key={item.label}
                    className="flex items-center gap-3 rounded-2xl border border-red-100 bg-white p-3 text-sm font-semibold text-slate-700 shadow-sm dark:border-red-400/10 dark:bg-white/[0.04] dark:text-white/75"
                  >
                    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-red-100 text-red-700 dark:bg-red-500/10 dark:text-red-200">
                      <Icon />
                    </span>
                    {item.label}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mt-6">
            <label
              htmlFor="delete-confirm-text"
              className="text-sm font-bold text-slate-800 dark:text-white"
            >
              Pour confirmer, saisissez{" "}
              <span className="rounded-lg bg-slate-100 px-2 py-1 font-bold text-red-700 dark:bg-white/10 dark:text-red-200">
                SUPPRIMER
              </span>
            </label>

            <input
              id="delete-confirm-text"
              type="text"
              value={confirmText}
              onChange={(event) => setConfirmText(event.target.value)}
              disabled={deleting}
              autoFocus
              className="mt-3 h-12 w-full rounded-2xl border border-slate-200 bg-white px-4 text-sm font-bold text-slate-900 outline-none transition placeholder:text-slate-400 focus:border-red-400 focus:ring-4 focus:ring-red-100 disabled:cursor-not-allowed disabled:opacity-60 dark:border-white/10 dark:bg-white/[0.05] dark:text-white dark:focus:border-red-300 dark:focus:ring-red-500/10"
              placeholder="SUPPRIMER"
            />
          </div>
        </div>

        <div className="flex flex-col-reverse gap-3 border-t border-slate-200 bg-slate-50 p-4 dark:border-white/10 dark:bg-white/[0.03] sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onClose}
            disabled={deleting}
            className="inline-flex min-h-12 items-center justify-center rounded-2xl border border-slate-200 bg-white px-5 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-white/10 dark:bg-white/[0.06] dark:text-white dark:hover:bg-white/10"
          >
            Annuler
          </button>

          <button
            type="submit"
            disabled={!canDelete || deleting}
            className="inline-flex min-h-12 items-center justify-center gap-2 rounded-2xl bg-red-600 px-5 py-3 text-sm font-bold text-white shadow-lg shadow-red-600/20 transition hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <FaTrash />
            {deleting ? "Suppression..." : "Supprimer définitivement"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default DeleteJudokaModal;